/**
 * GRI Standards Navigator
 * Tree-based navigation showing complete GRI structure
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BookOpen, ChevronRight, ChevronDown, Circle, CheckCircle,
  Building, Leaf, Users, DollarSign, ExternalLink, BarChart2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIStandardsNavigator = ({ study }) => {
  const [navigator, setNavigator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(['Universal Standards']);
  const [selectedStandard, setSelectedStandard] = useState(null);

  useEffect(() => {
    if (study?.id) {
      fetchNavigator();
    }
  }, [study?.id]);

  const fetchNavigator = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/standards-navigator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNavigator(res.data);
    } catch (error) {
      console.error('Error fetching standards navigator:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    if (expandedCategories.includes(categoryName)) {
      setExpandedCategories(expandedCategories.filter(c => c !== categoryName));
    } else {
      setExpandedCategories([...expandedCategories, categoryName]);
    }
  };

  const getCategoryIcon = (categoryName) => {
    if (categoryName.includes('Universal')) return <BookOpen className="w-5 h-5 text-indigo-600" />;
    if (categoryName.includes('Economic')) return <DollarSign className="w-5 h-5 text-green-600" />;
    if (categoryName.includes('Environmental')) return <Leaf className="w-5 h-5 text-emerald-600" />;
    if (categoryName.includes('Social')) return <Users className="w-5 h-5 text-blue-600" />;
    return <Circle className="w-5 h-5 text-gray-600" />;
  };

  const getCoverageColor = (coverage) => {
    if (coverage >= 80) return 'text-green-600 bg-green-50';
    if (coverage >= 50) return 'text-yellow-600 bg-yellow-50';
    if (coverage > 0) return 'text-orange-600 bg-orange-50';
    return 'text-gray-400 bg-gray-50';
  };

  const getCoverageBadge = (coverage) => {
    return (
      <div className={`flex items-center gap-1 px-2 py-1 rounded ${getCoverageColor(coverage)}`}>
        <BarChart2 className="w-3 h-3" />
        <span className="text-xs font-semibold">{coverage}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading GRI standards...</p>
        </div>
      </div>
    );
  }

  if (!navigator) {
    return <div className="text-center text-gray-600 py-12">No navigator data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">GRI Standards Navigator</h2>
        <p className="text-teal-100">Complete structure of GRI Standards with module linkages</p>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600">{navigator.summary.universal_standards}</div>
            <div className="text-sm text-gray-600">Universal</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{navigator.summary.economic_standards}</div>
            <div className="text-sm text-gray-600">Economic</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{navigator.summary.environmental_standards}</div>
            <div className="text-sm text-gray-600">Environmental</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{navigator.summary.social_standards}</div>
            <div className="text-sm text-gray-600">Social</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{navigator.summary.total_standards}</div>
            <div className="text-sm text-gray-600">Total Standards</div>
          </div>
        </div>
      </div>

      {/* Navigator Tree & Detail View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Tree Navigator */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-4 max-h-[800px] overflow-y-auto">
          <div className="space-y-2">
            {Object.entries(navigator.navigator).map(([categoryName, standards]) => (
              <div key={categoryName}>
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryName)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded-lg transition"
                >
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(categoryName)}
                    <span className="font-semibold text-gray-900 text-sm">{categoryName}</span>
                  </div>
                  {expandedCategories.includes(categoryName) ? 
                    <ChevronDown className="w-4 h-4 text-gray-600" /> : 
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  }
                </button>

                {/* Standards List */}
                {expandedCategories.includes(categoryName) && (
                  <div className="ml-4 mt-2 space-y-1">
                    {Object.entries(standards).map(([standardCode, standardData]) => (
                      <button
                        key={standardCode}
                        onClick={() => setSelectedStandard({ code: standardCode, ...standardData })}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition ${
                          selectedStandard?.code === standardCode 
                            ? 'bg-indigo-100 border-l-4 border-indigo-600' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="font-mono text-xs font-semibold text-gray-900">{standardCode}</div>
                          <div className="text-xs text-gray-600">{standardData.name}</div>
                        </div>
                        {standardData.coverage !== undefined && getCoverageBadge(standardData.coverage)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Standard Detail */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          {selectedStandard ? (
            <div className="space-y-6">
              {/* Standard Header */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-2xl font-bold text-indigo-600">{selectedStandard.code}</span>
                  {selectedStandard.mandatory && (
                    <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 rounded">
                      MANDATORY
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedStandard.name}</h3>
                {selectedStandard.description && (
                  <p className="text-gray-600">{selectedStandard.description}</p>
                )}
              </div>

              {/* Coverage */}
              {selectedStandard.coverage !== undefined && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-700">Data Coverage</span>
                    <span className="text-2xl font-bold text-indigo-600">{selectedStandard.coverage}%</span>
                  </div>
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                      style={{ width: `${selectedStandard.coverage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {selectedStandard.coverage >= 80 ? 'Excellent coverage - audit ready' :
                     selectedStandard.coverage >= 50 ? 'Good progress - some gaps remain' :
                     selectedStandard.coverage > 0 ? 'Getting started - more data needed' :
                     'No data available yet'}
                  </p>
                </div>
              )}

              {/* Linked Modules */}
              {selectedStandard.linked_modules && selectedStandard.linked_modules.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Linked SAARV Modules
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedStandard.linked_modules.map((module, idx) => (
                      <div key={idx} className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm flex items-center gap-2 border border-blue-200">
                        <Circle className="w-2 h-2 fill-current" />
                        {module}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    Data for this standard is automatically sourced from the above modules. Ensure these modules are 
                    complete and up-to-date for accurate GRI reporting.
                  </p>
                </div>
              )}

              {/* Disclosure Count */}
              {selectedStandard.disclosure_count && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Number of Disclosures:</span>
                    <span className="text-xl font-bold text-gray-900">{selectedStandard.disclosure_count}</span>
                  </div>
                </div>
              )}

              {/* System Rules (for GRI 3) */}
              {selectedStandard.code === 'GRI 3' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">⚠️ Control Plane Standard</h4>
                  <p className="text-sm text-purple-700">
                    GRI 3 (Material Topics) acts as the control plane for your entire GRI report. Topics identified 
                    as material here determine which Topic Standards (200, 300, 400 series) become mandatory for disclosure.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a standard from the navigator to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="text-sm text-teal-900">
          <p className="font-semibold mb-1">How the Navigator Works</p>
          <p>
            GRI Standards are organized into Universal (mandatory for all organizations) and Topic-specific standards 
            (mandatory only if the topic is material). SAARV automatically links operational data from various modules 
            to relevant GRI disclosures, maintaining a single source of truth for your sustainability reporting.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GRIStandardsNavigator;
